import jwt_decode from 'jwt-decode'
import { AVAILABLE_LANG_LIST, BASE_URL, AVAILABLE_MODALS_BY_QUERY, CONFIG } from 'src/common/config'
import { i18nObject } from 'boot/i18n'
import getISOCodes from 'iso-country-currency'
import * as Sentry from '@sentry/vue'
import { notification } from 'src/core/hooks/notifications'
import isbot from 'isbot'
import getAuthLink from 'src/apollo/queries/getAuthLink.gql'
import siteInfoMirrors from 'src/apollo/queries/siteInfoMirrors.gql'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { ApiServiceMain } from 'boot/api'
import { TokenService } from 'src/common/token-service'
import { getConfig } from 'src/configs'
import { useSystemStore } from 'src/stores/system'
import { useMainStore } from 'src/stores/main'

const config = getConfig()

export const isWebView = process.env.WEBVIEW === '1'

export function getAllInfoByISO(iso) {
  return getISOCodes.getAllInfoByISO(iso)
}

export function consoleLogAPI(error) {
  const isNetworkError = !!error.networkError
  const isGraphQlError = !!error.graphQLErrors
  const isApiError = error.name === 'AxiosError'

  if (isNetworkError || isGraphQlError || isApiError) {
    return
  }

  console.error(error)
  Sentry.captureException(error)
}

export function apiErrorHandler(e, notify = false, ownError = []) {
  if (e.status > 500) {
    return i18nObject.global.t('serverErrorTryAgain')
  }

  let error = ownError || [i18nObject.global.t('serverErrorTryAgain')]
  if (e.response && e.response.data) {
    const errorBody = e.response.data

    if (errorBody.non_field_errors) {
      const isErrorsList = errorBody.non_field_errors.length > 1

      let html = errorBody.non_field_errors[0]

      if (isErrorsList) {
        const errors = errorBody.non_field_errors.map((msg) => `<li>${msg}</li>`).join('')
        html = `<ul>
            ${errors}
        </ul>`
      }
      notification({ type: 'warning', text: html, html: isErrorsList })
      return
    }

    if (errorBody.detail) {
      if (notify) {
        notification({ type: 'warning', text: errorBody.detail })
      }
      return errorBody.detail
    }

    if (Object.keys(errorBody).length) {
      Object.entries(errorBody).forEach(([key, value]) => {
        if (this.errors !== undefined && this.errors[key] !== undefined) {
          if (Array.isArray(value)) {
            this.errors[key] = value
          } else {
            this.errors[key] = [value]
          }
        } else {
          error = value
        }
      })
    }
  } else if (e.toString().includes('timeout of')) {
    error = i18nObject.global.t('serverErrorTryAgain')
  }
  if (Array.isArray(error)) {
    error = error[0]
  }
  if (notify) {
    notification({ type: 'warning', text: error })
  }
  return error
}

export function toggleOverflowBody(value) {
  // TODO: Delete during refactoring
  if (process.env.SERVER) {
    return
  }
  if (value) {
    document.body.classList.add('q-body--prevent-scroll')
    document.body.classList.add('no-scroll')
  } else {
    document.body.classList.remove('q-body--prevent-scroll')
    document.body.classList.remove('no-scroll')
  }
}

export function toFixed(num, precision) {
  return (+(Math.round(+(num + 'e' + precision)) + 'e' + -precision)).toFixed(precision)
}

export function getGlyphCurrency(currency) {
  const currencyGlyphMap = {
    UZS: 'V',
    KGS: 'K',
  }

  return currencyGlyphMap[currency] || null
}

export function numberFilter(value, currency, minDigits = 0, maxDigits = 0, locale = 'ru-RU', notation = 'standard') {
  const newValue = Number(toFixed(value || 0, maxDigits))

  const glyphCurrency = getGlyphCurrency(currency)
  if (glyphCurrency) {
    currency = ''
  }

  const formatNumber = (options) => new Intl.NumberFormat(locale, options).format(newValue)

  const baseOptions = {
    minimumFractionDigits: minDigits > maxDigits ? maxDigits : minDigits,
    maximumFractionDigits: maxDigits,
    notation,
  }

  if (currency === false || !currency) {
    return glyphCurrency
      ? // eslint-disable-next-line no-irregular-whitespace
        `${formatNumber(baseOptions)} <span class='currencies-font'>${glyphCurrency}</span>`
      : formatNumber(baseOptions)
  }

  try {
    return formatNumber({
      ...baseOptions,
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
  } catch {
    return `${formatNumber(baseOptions)} ${currency || ''}`.trim()
  }
}

export function getCurrencySymbol(currency, locale = 'ru-RU', currencyDisplay = '') {
  try {
    const options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }

    if (currency) {
      options.currency = currency
      options.style = 'currency'

      if (currencyDisplay) {
        options.currencyDisplay = currencyDisplay
      }
    }

    return (0).toLocaleString(locale, options).replace(/\d/g, '').trim()
  } catch {
    return ''
  }
}

export const dateToString = (date) => {
  let year = date.getFullYear()
  let month = String(date.getMonth() + 1).padStart(2, '0')
  let day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const dateFilter = (value, format = 'dateshort', locale = 'ru-ru') => {
  if (!locale) {
    locale = 'ru-RU'
  }
  if (!value) {
    return ''
  }

  const options = {}
  if (format.includes('datewithoutsecond')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
    options.hour = '2-digit'
    options.minute = '2-digit'
  } else if (format.includes('datetimeshort')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  } else if (format.includes('dateshort')) {
    options.day = '2-digit'
    options.month = '2-digit'
    options.year = 'numeric'
  } else if (format.includes('date')) {
    options.day = '2-digit'
    options.month = 'long'
    options.year = 'numeric'
  }
  if (format.includes('time')) {
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  }
  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(value))
  } catch {
    console.error('Invalid date format: ', value)
    return ''
  }
}

export function actionToken(token) {
  try {
    const decoded = jwt_decode(token)

    const exp = decoded.exp * 1000
    const refreshTime = 60 * 60 * 24 * 1000 // 1 сутки
    const dateNow = Date.now()

    if (dateNow > exp) {
      return 'remove'
    } else if (dateNow > exp - refreshTime) {
      return 'refresh'
    }
    return 'none'
  } catch {
    return 'remove'
  }
}

export function getAxiosOptionsURL() {
  let baseURL = `${location.origin}${BASE_URL}${process.env.VUE_APP_API_URL}`

  return {
    baseURL,
    timeout: CONFIG.API.TIMEOUT,
    headers: {
      Vary: 'Accept-Encoding',
    },
  }
}

export function getGqlURL() {
  return `${location.origin}${BASE_URL}${process.env.GRAPHQL_URI}`
}

export function checkDataFromBack(text) {
  return text ? text.trim().length > 0 : false
}

export function changeTitleAndClearTemplatesMeta(meta, title) {
  meta.title = title
  meta.titleTemplate = (title) => title
  meta.meta.ogTitle.content = title
  meta.meta.ogTitle.template = (title) => title
}

export function changeDescriptionMeta(meta, content) {
  meta.meta.description = {
    name: 'description',
    content,
  }
  meta.meta.ogDescription = {
    name: 'og:description',
    content,
  }
}

export function capitalizeFirstLetter(string) {
  if (string && string.length > 0) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }
  return string
}

export function configurationCookiesConfig(token, config) {
  try {
    const decoded = jwt_decode(token)
    const exp = decoded.exp

    return { ...config, expires: new Date(exp * 1000) }
  } catch {
    return { ...config }
  }
}

export function testChunkFailed(text) {
  // const regex = /^(ChunkLoadError):.*(Loading chunk).+(failed)\.?$/gm; // Only one string
  const regex = /.*ChunkLoadError:.*Loading chunk.*/
  return regex.test(text)
}

export function notifyIfChunkError(error) {
  if (testChunkFailed(error)) {
    if (useSystemStore().countOfErrorsChunkLoaded === 0) {
      const actions = [
        {
          label: i18nObject.t('refresh'),
          color: 'white',
          handler: () => {
            location.reload()
          },
        },
      ]
      notification({ type: 'warning', timeout: 15000, text: i18nObject.t('newVersionReloadPage'), actions })
    }
    useSystemStore().updateErrorChunks()
    return true
  }
  return false
}

export function getAvailableLangFromUA(languages) {
  if (!languages) {
    return null
  }
  return languages.map((lang) => lang.slice(0, 2)).find((lang) => AVAILABLE_LANG_LIST.includes(lang))
}

export const defaultLang = process.env.DEFAULT_LANG || 'ru'

export const getRedirectLocale = () => {
  const isBot = isbot(navigator.userAgent) || navigator.userAgent.includes('prerender')
  const newDefaultLang = location.hostname === 'olymp-casinobd.com' ? 'en' : defaultLang
  if (isBot) {
    return newDefaultLang
  }

  const langFromLS = localStorage.getItem('u_lang')
  const userLang =
    langFromLS && AVAILABLE_LANG_LIST.includes(langFromLS.toLowerCase()) ? langFromLS.toLowerCase() : null
  return userLang ? userLang : getAvailableLangFromUA(navigator.languages) || newDefaultLang
}

export function getLocale() {
  let clearPath = location.pathname.replace(BASE_URL, '')
  const regEx = /^\/(?<lang>[a-z]{2})(?:[^a-zA-Z]|$)/
  const parseResult = regEx.exec(clearPath)

  let localeFromURL = null
  if (parseResult && parseResult.groups) {
    localeFromURL = parseResult.groups.lang
  }

  return getLang(localeFromURL)
}

export function getLang(langFromURL) {
  if (langFromURL) {
    const uLang = localStorage.getItem('u_lang')
    const langFromURLModify = langFromURL.toLowerCase()

    if (uLang && AVAILABLE_LANG_LIST.includes(uLang.toLowerCase()) && langFromURLModify !== uLang.toLowerCase()) {
      return uLang.toLowerCase()
    }

    if (AVAILABLE_LANG_LIST.includes(langFromURLModify)) {
      return langFromURLModify
    }
  }

  return getRedirectLocale()
}

function toCamel(string) {
  return string.replace(/([-_][a-z])/gi, ($1) => $1.toUpperCase().replace('-', '').replace('_', ''))
}

function isObject(args) {
  return args === Object(args) && !Array.isArray(args) && typeof args !== 'function'
}

export const renameKeys = (args) => {
  if (isObject(args)) {
    const n = {}

    Object.keys(args).forEach((k) => {
      n[toCamel(k)] = renameKeys(args[k])
    })

    return n
  }
  if (Array.isArray(args)) {
    return args.map((i) => renameKeys(i))
  }

  return args
}

export const partitionTwoArrayByFilter = (array, filter) => {
  let one = [],
    two = []
  array.forEach((e, idx, arr) => (filter(e, idx, arr) ? one : two).push(e))
  return [one, two]
}

export const getAppToken = (clear = false) => {
  if (!config.app.useToken) {
    return { token: '', type: null, mb_uuid: null }
  }

  const token = useMainStore().getAssid
  const type = useMainStore().getAppType
  const mb_uuid = useMainStore().getMbUuid

  if (clear) {
    useMainStore().clear()
  }

  return { token, type, mb_uuid }
}

export const clearTrailingSlash = (url) => {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

export function hasWhiteSpace(s) {
  return /\s/g.test(s)
}

export const withoutCyrillic = (value) => !/\p{Script=Cyrl}/u.test(value)

export const preloaderVisible = (val) => {
  const className = 'ready'
  const preLoaderClassName = '.main-preloader'
  const body = document.body

  const preloaderEl = document.querySelector(preLoaderClassName)

  if (!val) {
    if (preloaderEl) {
      preloaderEl.remove()
    }
    if (!body.classList.contains(className)) {
      body.classList.add(className)
    }
  } else {
    body.classList.remove(className)
  }
}

export function defineSportNameBySportId(sportId) {
  switch (sportId) {
    case 1:
      return 'football'
    case 2:
      return 'hockey'
    case 3:
      return 'tennis'
    case 5:
      return 'basketball'
    case 9:
      return 'handball'
    case 10:
      return 'volleyball'
    default:
      return null
  }
}

export const cdnDomain = process.env.CDN_DOMAIN_BACK || ''

export function getUrlWithModifier(url, modifier) {
  if (!url) {
    return ''
  }
  if (!modifier) {
    return cdnDomain + url
  }
  const segments = url.split('/')
  const filename = segments.pop()
  const modifiedFilename = `${modifier}/${filename}`
  segments.push(modifiedFilename)
  return cdnDomain + segments.join('/')
}

export function getCompleteSrcset(url) {
  return `${cdnDomain}${url} 1x, ${getUrlWithModifier(url, 2)} 2x, ${getUrlWithModifier(url, 3)} 3x`
}

export const itemIsExist = (item) => item !== null && item !== '' && item !== undefined

export function getResultQuery(routeQuery, additionalQuery = {}) {
  const data = Object.fromEntries(
    Object.entries({ ...routeQuery, ...additionalQuery }).filter((item) => itemIsExist(item[1]))
  )
  return new URLSearchParams(data).toString()
}

export const openNewMirror = async (mirror, apollo, query = {}, locale = null) => {
  const tokenService = new TokenService()

  const { data: authData } = await apollo.query({
    query: getAuthLink,
    fetchPolicy: 'network-only',
  })

  const { uuid, uuidSecond, hashStr } = authData.authLink
  const queryPath = getResultQuery({ ...query, hashstr: hashStr })
  const prefix = locale ? `/${locale}` : ''
  const linkPath = `${prefix}/accounts/login/${uuid}/${uuidSecond}${queryPath ? '?' + queryPath : ''}`

  await tokenService.removeToken()
  sessionStorage.removeItem('linkAfterAuth')
  sessionStorage.removeItem('redirect2cda')

  window.open('https://' + mirror + linkPath, '_self')
}

export async function checkMirrorAndRedirect(apollo, query = {}, locale = null) {
  const simpleRedirect = () => {
    let path = null

    if (query?.path) {
      path = decodeURIComponent(query.path)
    }

    sessionStorage.removeItem('linkAfterAuth')
    if (path && path.startsWith('/')) {
      location.href = `${location.origin}${BASE_URL}${path}`
    } else {
      location.reload()
    }
  }

  if (isWebView || process.env.DEV || process.env.ENVIRONMENT === 'development' || !config.useMirrors) {
    return simpleRedirect()
  }

  return new Promise(async (resolve) => {
    try {
      const { data } = await apollo.query({
        query: siteInfoMirrors,
        fetchPolicy: 'network-only',
      })

      const { domain, mirrorDomain } = data.siteInfo
      if (domain === mirrorDomain) {
        return simpleRedirect()
      }

      await openNewMirror(mirrorDomain, apollo, query, locale)
    } catch (e) {
      consoleLogAPI(e)
      simpleRedirect()
    } finally {
      resolve()
    }
  })
}

export async function checkMirror(apollo, query = {}, locale = null) {
  return new Promise(async (resolve) => {
    if (isWebView || process.env.DEV) {
      return resolve(false)
    }

    try {
      const { data } = await apollo.query({
        query: siteInfoMirrors,
        fetchPolicy: 'network-only',
      })

      const { domain, mirrorDomain } = data.siteInfo
      if (domain === mirrorDomain) {
        return resolve(false)
      }

      await openNewMirror(mirrorDomain, apollo, query, locale)
    } catch (e) {
      consoleLogAPI(e)
      resolve(false)
    } finally {
      resolve(false)
    }
  })
}

export async function getFingerprint() {
  const LS_KEY = 'fp_k'
  try {
    if (localStorage.getItem(LS_KEY)) {
      return localStorage.getItem(LS_KEY)
    }
    // const fpPromise = await FingerprintJS.load({ apiKey: process.env.FINGERPRINT })
    const fpPromise = await FingerprintJS.load()
    const result = await fpPromise.get()

    localStorage.setItem(LS_KEY, result.visitorId)
    return result.visitorId
  } catch (e) {
    console.error(e)
    return null
  }
}

export function getNewRouteByLang(to, router) {
  //TODO: продумать логику чтобы рут локация работала только с DEFAULT_LANG
  const lang = getLang(to.params.locale)
  const currenLang = location.hostname === 'olymp-casinobd.com' ? 'en' : defaultLang
  const isDefaultLang = lang === currenLang

  if (to.params.locale !== lang || isDefaultLang) {
    const currentRoute = { ...to }
    currentRoute.params = { ...currentRoute.params, locale: lang }

    if (isDefaultLang) {
      if (useSystemStore().alt && useSystemStore().isBot) {
        return null
      }

      delete currentRoute.params.locale
    }

    try {
      return router.resolve(currentRoute)
    } catch (e) {
      console.error(e)
      return null
    }
  }
  return null
}

export const sendDebugInfoToSentry = (message, data = {}, level = 'debug') => {
  try {
    Sentry.withScope((scope) => {
      const extras = data?.extras ?? {}
      const tags = data?.tags ?? {}

      for (const item in extras) {
        scope.setExtra(item, extras[item])
      }
      for (const item in tags) {
        scope.setTag(item, tags[item])
      }
      scope.setLevel(level)

      Sentry.captureMessage(message)
    })
  } catch (e) {
    console.error(e)
  }
}

export function captureCustomEvent(eventType, details) {
  Sentry.captureEvent({
    message: eventType,
    level: 'info',
    tags: {
      eventType: eventType,
    },
    extra: details,
  })
}

export const loggingWebview = (type, user_id) => {
  const initPath = localStorage.getItem('wv-init-href')

  sendDebugInfoToSentry('Mobile Webview Auth Event', {
    extras: {
      initPath: initPath,
      currentPath: location.href,
    },
    tags: {
      webview: type,
      hostname: location.hostname,
      user_id: user_id || 'none',
    },
  })
}

export const getCurrencyByCountry = (countryCode, defaultCountryCode = 'KZT') => {
  const lowercaseCode = countryCode?.toLowerCase() ?? ''

  const countryCurrencyMap = {
    kz: 'KZT',
    kg: 'KGS',
    uz: 'UZS',
    in: 'INR',
    ru: 'RUB',
    bd: 'BDT',
    az: 'AZN',
  }

  if (countryCurrencyMap[lowercaseCode]) {
    return countryCurrencyMap[lowercaseCode]
  }

  return defaultCountryCode
}

export const getModal = (name, hasAuth) => {
  const modal = AVAILABLE_MODALS_BY_QUERY[name]
  if (!modal) {
    return
  }
  if (modal.authRequire && !hasAuth) {
    return AVAILABLE_MODALS_BY_QUERY.login
  }
  if (modal.authRequire === hasAuth || modal.authRequire === undefined) {
    return modal
  }
}

export const clearSlugAtCDA = async () => {
  return new Promise((resolve) => {
    const image = document.createElement('img')
    image.setAttribute('src', `${process.env.VUE_APP_CDA_URL}slug/?eat_slug=true&url=${location.href}`)
    image.setAttribute('style', 'position: absolute; left: -9999px; opacity: 0')
    image.classList.add('cda-img')

    image.addEventListener('error', function () {
      resolve()
      document.querySelector('img.cda-img').remove()
    })
    image.addEventListener('load', function () {
      resolve()
      document.querySelector('img.cda-img').remove()
    })

    document.body.appendChild(image)
  })
}

export const clearQueryAfterAuth = (query, extra = ['path', 'url']) => {
  const resultQuery = { ...query }
  delete resultQuery.rand_d
  delete resultQuery.hashstr

  extra.forEach((param) => delete resultQuery[param])

  return resultQuery
}

export const setUssidToCookies = (ussid, expire) => {
  if (!config.authBySession) {
    return
  }
  return ApiServiceMain.auth
    .setSession({
      ussid,
      ussidt: Date.now().toString(),
      expire,
    })
    .catch(consoleLogAPI)
}

export const removeUssIdFromCookies = () => {
  if (!config.authBySession) {
    return
  }
  return ApiServiceMain.auth.removeSession().catch(consoleLogAPI)
}

export const fillPromoItems = (items, types) => {
  let result = []
  if (items?.length && types) {
    result = items
      .map((promotionItem) => {
        const parsedProps = JSON.parse(promotionItem.props)
        const promotionTypeProps = types.find((i) => +i.id === +parsedProps.promotionTypeId)

        return {
          id: promotionItem.id,
          dateStart: promotionItem.dateStart,
          dateEnd: promotionItem.dateEnd,
          images: promotionItem.images,
          props: {
            ...parsedProps,
          },
          promotionTypeProps,
        }
      })
      .filter((promotionItem) => promotionItem.props.active)
      .sort((a, b) => a.props.order - b.props.order)
  }
  return result
}

export function getSocialLinksByRegion(region) {
  const socialLinks = config.socialLinks

  if (region) {
    const formattedRegion = region.toLowerCase().trim()
    return { ...socialLinks.default, ...(socialLinks.region?.[formattedRegion] ?? {}) }
  }

  return socialLinks.default
}

export const hexToShortRGB = (hex) => {
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let color
    color = hex.substring(1).split('')

    if (color.length == 3) {
      color = [color[0], color[0], color[1], color[1], color[2], color[2]]
    }

    color = '0x' + color.join('')

    return [(color >> 16) & 255, (color >> 8) & 255, color & 255].join(',')
  }

  return null
}
